/**
 * `cookie_consent_update`: the visitor's consent decision as a dataLayer event.
 *
 * Google Consent Mode (`gtag('consent', …)`) tells Google tags how to behave,
 * but it gives Google Tag Manager nothing to *trigger* on. Container
 * GTM-5PKXQKHR blocks the Google tag until analytics consent is granted, and all
 * of its triggers fire at container start, before the banner has been answered
 * — so without an event published after the decision the tag never fires
 * (Q-38 in docs/QUALITY.md). This is that event. The container side lives in
 * docs/gtm/.
 *
 * Published only once the visitor has decided: an undecided first-time visitor
 * would otherwise be published as "analytics false / marketing false", which is
 * indistinguishable from an explicit refusal — and, because a state is published
 * once, their actual "Reject optional" click a moment later would be swallowed
 * as a repeat.
 *
 * Published once per distinct state: vanilla-cookieconsent calls onFirstConsent
 * and onConsent for the same click, and two events would fire the tag twice.
 */

export const CONSENT_UPDATE_EVENT = 'cookie_consent_update';

export interface ConsentUpdateEvent {
  event: typeof CONSENT_UPDATE_EVENT;
  cookie_consent_analytics: boolean;
  cookie_consent_marketing: boolean;
}

interface ConsentPublisherDeps {
  /** Whether the visitor has made a decision at all (a stored, valid consent). */
  hasDecided: () => boolean;
  hasAnalyticsConsent: () => boolean;
  hasMarketingConsent: () => boolean;
  /** The dataLayer to push into, resolved at call time so it can be created lazily. */
  dataLayer: () => unknown[];
}

/**
 * Returns a function that publishes the current consent state, or declines
 * because the visitor has not decided yet or because that exact state was
 * already published. Returns whether it pushed.
 */
export const createConsentPublisher = ({
  hasDecided,
  hasAnalyticsConsent,
  hasMarketingConsent,
  dataLayer,
}: ConsentPublisherDeps): (() => boolean) => {
  let lastPublished: string | null = null;

  return () => {
    if (!hasDecided()) {
      return false;
    }

    const event: ConsentUpdateEvent = {
      event: CONSENT_UPDATE_EVENT,
      cookie_consent_analytics: hasAnalyticsConsent(),
      cookie_consent_marketing: hasMarketingConsent(),
    };
    const state = `${event.cookie_consent_analytics}/${event.cookie_consent_marketing}`;

    if (state === lastPublished) {
      return false;
    }

    dataLayer().push(event);
    lastPublished = state;

    return true;
  };
};
